import axios from 'axios';
import { supabase } from '../lib/supabase';

// UAZAPI Configuration
const UAZAPI_BASE_URL = process.env.REACT_APP_UAZAPI_BASE_URL || 'https://solvion.uazapi.com';

// Instance status interface aligned with UAZAPI states
export interface InstanceStatus {
  status: 'CONNECTED' | 'DISCONNECTED' | 'QRCODE' | 'TIMEOUT' | 'ERROR';
  instanceToken?: string; // Token único da instância (api_key no banco)
  instanceName?: string;
}

// UAZAPI Instance response type
interface UazapiInstance {
  id: string;
  token: string;
  status: string; // 'disconnected' | 'connecting' | 'connected'
  paircode?: string;
  qrcode?: string; // base64 data URL
  name: string;
  profileName?: string;
  profilePicUrl?: string;
}


interface UazapiConnectResponse {
  connected: boolean;
  loggedIn: boolean;
  jid: string | null;
  instance: UazapiInstance;
}

/**
 * Gera o nome único da instância usando o ID do usuário.
 */
export async function getUniqueInstanceName(baseName: string): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }
  const userIdPrefix = user.id.substring(0, 8);
  const sanitizedBaseName = baseName.toLowerCase().replace(/\s/g, '-');
  return `scaliz-${userIdPrefix}-${sanitizedBaseName}`;
}

/**
 * 1. Cria uma nova instância na UAZAPI.
 * @param uniqueInstanceName Nome único da instância.
 * @returns Token da instância criada.
 */
export async function createInstance(uniqueInstanceName: string): Promise<{ instanceToken: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Sessão não encontrada. Faça login novamente.');
    }

    const { data, error } = await supabase.functions.invoke('create-uazapi-instance', {
      body: { uniqueInstanceName }
    });

    if (error) {
      throw error;
    }

    if (!data || !data.instanceToken) {
      throw new Error('Token da instância não retornado pela Edge Function.');
    }

    return { instanceToken: data.instanceToken };

  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Falha ao criar instância: ${error.message}`);
    }
    throw new Error('Falha ao criar a instância do WhatsApp.');
  }
}

/**
 * 2. Conecta uma instância ao WhatsApp, retornando QR code ou paircode.
 * @param instanceToken Token único da instância.
 * @param phone Número de telefone opcional (gera paircode se fornecido, QR code caso contrário).
 * @returns Objeto com QR code (base64) ou paircode.
 */
export async function connectInstance(
  instanceToken: string,
  phone?: string
): Promise<{ qrCodeUrl?: string; pairCode?: string }> {
  try {
    const body: Record<string, string> = {};
    if (phone) {
      body.phone = phone;
    }

    const response = await axios.post<UazapiConnectResponse>(
      `${UAZAPI_BASE_URL}/instance/connect`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'token': instanceToken,
        },
        timeout: 30000,
      }
    );

    const instance = response.data.instance;

    if (instance?.qrcode) {
      return { qrCodeUrl: instance.qrcode };
    }

    if (instance?.paircode) {
      return { pairCode: instance.paircode };
    }

    // Já conectado
    if (response.data.connected || response.data.loggedIn) {
      return {};
    }

    return {};

  } catch (error) {
    console.error('[UAZAPI] Error connecting instance:', error);
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      const message = errorData?.message || errorData?.error || JSON.stringify(errorData);
      throw new Error(`Falha ao conectar instância: ${message}`);
    }
    throw new Error(error instanceof Error ? error.message : 'Falha ao conectar a instância do WhatsApp.');
  }
}

/**
 * 3. Verifica o status de conexão de uma instância.
 * @param instanceToken Token único da instância.
 * @returns Objeto com o status da conexão.
 */
export async function checkInstanceStatus(instanceToken: string): Promise<InstanceStatus> {
  try {
    const response = await axios.get<{ instance: UazapiInstance; connected: boolean; loggedIn: boolean }>(
      `${UAZAPI_BASE_URL}/instance/status`,
      {
        headers: {
          'Accept': 'application/json',
          'token': instanceToken,
        },
        timeout: 15000,
      }
    );

    const data = response.data;
    const instanceStatus = data?.instance?.status || '';
    const isConnected = data?.connected || data?.loggedIn || instanceStatus === 'connected';
    const isConnecting = instanceStatus === 'connecting';

    if (isConnected) {
      return {
        status: 'CONNECTED',
        instanceToken,
        instanceName: data?.instance?.name,
      };
    }

    if (isConnecting) {
      return {
        status: 'QRCODE',
        instanceToken,
        instanceName: data?.instance?.name,
      };
    }

    return {
      status: 'DISCONNECTED',
      instanceToken,
      instanceName: data?.instance?.name,
    };

  } catch (error) {
    console.error('[UAZAPI] Error checking instance status:', error);
    return { status: 'ERROR', instanceToken };
  }
}

/**
 * 4. Deleta e desconecta a instância do WhatsApp na UAZAPI.
 * Este endpoint encerra a sessão e remove a instância permanentemente.
 * @param instanceToken Token único da instância.
 */
export async function disconnectInstance(instanceToken: string): Promise<void> {
  try {
    await axios.delete(
      `${UAZAPI_BASE_URL}/instance`,
      {
        headers: {
          'Accept': 'application/json',
          'token': instanceToken,
        },
        timeout: 15000,
      }
    );
  } catch (error) {
    console.error('[UAZAPI] Error deleting instance:', error);
    throw new Error('Falha ao remover a instância do WhatsApp.');
  }
}